import React, {createContext, PropsWithChildren, useCallback, useContext, useMemo, useState} from "react";
import {Button, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {AdoptIdentityChange} from "../../../../api/ElevationTool";

// "update": apply the changes to the opening; "copy": link a private copy of the
// elevation carrying the opening's own identity (offered only when the server says the
// diff is copy-safe, e.g. a handing flip on a single).
export type AdoptIdentityResolution = "update" | "copy";

type ConfirmState = {
    changes: AdoptIdentityChange[];
    canCopy: boolean;
    // resolves to the chosen resolution, or null if the user cancelled
    resolve(resolution: AdoptIdentityResolution | null): void;
}

const ConfirmAdoptIdentityContext = createContext({
    confirm: async (changes: AdoptIdentityChange[], canCopy: boolean): Promise<AdoptIdentityResolution | null> => null,
});

export function useConfirmAdoptIdentity() {
    return useContext(ConfirmAdoptIdentityContext);
}

// Prompts before an elevation adopt (paste / dropdown pick) that would overwrite opening
// properties (e.g. handing) to match the elevation — the server returns the diff without
// saving, this dialog confirms it, and the caller re-sends the adopt. Styled after the
// elevation tool's SharedElevationDialog.
export function ConfirmAdoptIdentityProvider(props: PropsWithChildren<{}>) {
    const [state, setState] = useState<ConfirmState | null>(null);

    const confirm = useCallback((changes: AdoptIdentityChange[], canCopy: boolean) => {
        return new Promise<AdoptIdentityResolution | null>((resolve) => {
            setState({changes, canCopy, resolve});
        });
    }, []);

    const ctx = useMemo(() => ({confirm}), [confirm]);

    const done = (resolution: AdoptIdentityResolution | null) => {
        state?.resolve(resolution);
        setState(null);
    };

    return (
        <ConfirmAdoptIdentityContext.Provider value={ctx}>
            {props.children}
            {state && (
                <Dialog2 open onClose={() => done(null)}>
                    <DialogTitle>Update opening</DialogTitle>
                    <DialogContent>
                        <DialogContentText style={{minWidth: 380}}>
                            This elevation does not match the opening. Linking it updates these
                            opening properties:
                        </DialogContentText>
                        <ul style={{marginBottom: 0}}>
                            {state.changes.map(c => (
                                <li key={c.field}>{c.field}: {c.from || "(blank)"} &rarr; {c.to || "(blank)"}</li>
                            ))}
                        </ul>
                        {state.canCopy && (
                            <DialogContentText style={{marginTop: 12}}>
                                Copy a version links a private copy of the elevation matched to this
                                opening instead — the opening keeps its properties.
                            </DialogContentText>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => done(null)}>Cancel</Button>
                        {state.canCopy && (
                            <Button onClick={() => done("copy")}>Copy a version</Button>
                        )}
                        <Button variant="contained" onClick={() => done("update")}>Update opening</Button>
                    </DialogActions>
                </Dialog2>
            )}
        </ConfirmAdoptIdentityContext.Provider>
    );
}
